import {
  Button,
  Icon,
  Popover,
  SegControl,
  Text,
} from '@lightspeed/design-system-react'
import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { T, useT } from '@transifex/react'
import { useDispatch, useSelector } from 'react-redux'
import { calculateMaxDisplayDiscount } from '../../../sale-calculation/calculate-sale'
import {
  focusGlobalSearch,
  useSaleManager,
  useUserManager,
  useVdTracking,
} from '../../../ng-compat/hooks'
import { setSaleDiscountIsPercentage } from '../../../managers/wr-session-storage'
import { updateDisplayDiscount } from '../../../state/slices/currentSale/currentSaleSlice'

import {
  selectDiscountInfo,
  selectSaleReturnFor,
} from '../../../state/selectors'
import { useDiscountInvalid } from '../sale-footer/useDiscountInvalid'
import { useCurrencySymbol } from './useCurrencySymbol'
import { DiscountInputRef, SaleDiscountInput } from './SaleDiscountInput'
import { DiscountPopoverActions } from './DiscountPopoverActions'

type Props = {
  closePopover: () => void

  /** FIXME: make this required fields after mgiration to redux is completed */
  removeSaleDiscount?: () => void
}
export const DiscountPopover = (props: Props) => {
  const { closePopover } = props
  const saleManager = useSaleManager()
  const userManager = useUserManager()
  const currencySymbol = useCurrencySymbol()
  const vdTracking = useVdTracking()
  const dispatch = useDispatch()
  const discountInvalid = useDiscountInvalid()

  const {
    displayDiscount,
    discountIsPercentage: initialDiscountIsPercentage,
    maxSaleDiscount: initialMaxSaleDiscount,
  } = useSelector(selectDiscountInfo)

  const [discountIsPercentage, setDiscountIsPercentage] = useState(
    initialDiscountIsPercentage
  )
  const [maxSaleDiscount, setMaxSaleDiscount] = useState<string | number>(() =>
    calculateMaxDisplayDiscount({
      discountableTotal: initialMaxSaleDiscount,
      discountIsPercentage,
      maxPercentageDiscount: userManager.getMaxSaleDiscountPercent(),
    })
  )
  const discountInputRef = useRef<DiscountInputRef>(null)
  const t = useT()
  /**
   * When mounted, and when discount type changed, select the text in the discount input
   */
  useEffect(() => {
    discountInputRef.current?.select()
  }, [discountIsPercentage])

  /**
   * when click on the seg control, highlight the text in the discount input
   */
  const clickDiscountType = () => discountInputRef.current?.select()

  /**
   * Handler when discount type changed from % to $ and vice versa
   */
  const changeDiscountType = (isPercentage: boolean) => {
    const discountableTotal = initialMaxSaleDiscount
    const maxPercentageDiscount = userManager.getMaxSaleDiscountPercent()
    const maxSaleDiscountAllowed = calculateMaxDisplayDiscount({
      discountableTotal,
      discountIsPercentage: isPercentage,
      maxPercentageDiscount,
    })

    setDiscountIsPercentage(isPercentage)
    setMaxSaleDiscount(maxSaleDiscountAllowed)
  }

  const addSaleDiscount = () => {
    if (!discountInputRef.current || addBtnDisabled) {
      return
    }
    const displayDiscount = discountInputRef.current.getValue()

    const eventName = discountIsPercentage
      ? 'sale.sale-discount.percent-changed'
      : 'sale.sale-discount.amount-changed'

    vdTracking.trackUserEvent({
      eventName,
      metadata: {
        valueSet: displayDiscount,
      },
    })

    /**
     * dispatch redux action to update displayDiscount & discountIsPercentage instead of mutating sale object directly
     */
    dispatch(updateDisplayDiscount({ displayDiscount, discountIsPercentage }))

    // when sale discount updated, close the popup, focus on the global search and set the discountIsPercentage in session storage
    saleManager.updateSaleDiscount().then(() => {
      closePopover()
      focusGlobalSearch()
      // Set the discountIsPercentage session storage value
      setSaleDiscountIsPercentage(discountIsPercentage)
    })
  }

  const [addBtnDisabled, setAddBtnDisabled] = useState(false)
  const onDiscountValid = (isValid: boolean) => setAddBtnDisabled(!isValid)
  const returnFor = useSelector(selectSaleReturnFor)
  const isReturn = Boolean(returnFor)

  return (
    <Popover
      onClose={closePopover}
      className="sale-popover wr-discount-popover"
      actions={
        <DiscountPopoverActions
          handleCancel={closePopover}
          handleAdd={addSaleDiscount}
          addBtnDisabled={addBtnDisabled || discountInvalid}
        />
      }
    >
      {discountInvalid ? (
        <h1 className="vd-text-label">
          <T _str="Remove discount" _context="Add discount popover header" />
        </h1>
      ) : (
        <h1 className="vd-text-label">
          <T _str="Add discount" _context="Add discount popover header" />
        </h1>
      )}
      <hr className="vd-hr"></hr>
      <form className="vd-flex vd-flex--column">
        {!discountInvalid ? (
          <div className="vd-flex vd-flex--align-start">
            {/* Discount input type segcontrol */}
            <SegControl
              name="discountInputType"
              onChange={() => changeDiscountType(true)}
              onClick={clickDiscountType}
              checked={discountIsPercentage}
              className={classNames(
                'vd-segcontrol wr-sale-discount-percentage-segcontrol',
                { 'vd-segcontrol--selected ': discountIsPercentage }
              )}
              role="radio"
            >
              %
            </SegControl>
            <SegControl
              name="discountInputType"
              onChange={() => changeDiscountType(false)}
              onClick={clickDiscountType}
              checked={!discountIsPercentage}
              className={classNames(
                'vd-segcontrol wr-sale-discount-currency-segcontrol vd-mr4',
                {
                  'vd-segcontrol--selected': !discountIsPercentage,
                }
              )}
              role="radio"
            >
              {currencySymbol}
            </SegControl>
            {/* Discount input */}
            <SaleDiscountInput
              discountIsPercentage={discountIsPercentage}
              maxSaleDiscount={maxSaleDiscount}
              displayDiscount={displayDiscount}
              saleIsReturn={isReturn}
              currencySymbol={currencySymbol}
              onValidChange={onDiscountValid}
              ref={discountInputRef}
              containerCss={'wr-sale-discount-input-container'}
              onEnter={addSaleDiscount}
            />
            <Button
              className="vd-ml1 vd-mr0"
              modifier="icon"
              variant="no"
              aria-label="Remove discount"
              onClick={props.removeSaleDiscount}
            >
              <Icon icon="trash" />
            </Button>
          </div>
        ) : null}

        {discountInvalid ? (
          <>
            <div className="vd-flex vd-flex--align-center vd-flex--justify-between">
              <Text>
                {displayDiscount} {discountIsPercentage ? '%' : null}
              </Text>

              <Button
                className="vd-ml1 vd-mr0"
                modifier="icon"
                variant="no"
                aria-label="Remove discount"
                onClick={props.removeSaleDiscount}
              >
                <Icon icon="trash" />
              </Button>
            </div>
          </>
        ) : null}

        <div className="vd-text--sub vd-text--secondary vd-mt3 vd-mb3">
          {t('This discount will be applied to all items', {
            _context: 'sale discount info',
          })}
        </div>
      </form>
    </Popover>
  )
}
